/* eslint-disable react-hooks/exhaustive-deps */
"use client";

// Task 7.5: Pricing Table Component - Enterprise-Grade 3-Column Design

import React, { useState, useEffect, useMemo } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, Sparkles, ShieldCheck, ArrowRight, Building2 } from 'lucide-react';
import { CurrencyManager } from '@/lib/marketing/currency-manager';
import { formatPrice } from '@/lib/marketing/currency-utils';
import { storeCurrencyPreference, getCurrencyPreference } from '@/lib/marketing/preferences';
import { track } from '@/lib/analytics';

type BillingCycle = 'monthly' | 'annual';
type PlanTier = 'STARTER' | 'PRO';

interface PricingTableProps {
  /**
   * Initial currency hint derived from marketing middleware.
   */
  initialCurrency?: string;
}

export function PricingTable({ initialCurrency }: PricingTableProps) {
  const t = useTranslations('marketing.pricing');
  const locale = useLocale();
  const [selectedCurrency, setSelectedCurrency] = useState<string>(() => {
    const manager = new CurrencyManager();
    if (initialCurrency && manager.isValidCurrency(initialCurrency)) return initialCurrency;
    const stored = getCurrencyPreference();
    if (stored && manager.isValidCurrency(stored)) return stored;
    const l = (typeof locale === 'string' ? locale : 'en-GB').toLowerCase();
    if (l.startsWith('en-gb')) return 'GBP';
    if (l.startsWith('en-ie')) return 'EUR';
    if (l.startsWith('de-ch')) return 'CHF';
    if (l.startsWith('de-de') || l.startsWith('de-at')) return 'EUR';
    return 'GBP';
  });
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const currencyManager = useMemo(() => new CurrencyManager(), []);

  useEffect(() => {
    if (initialCurrency) return;
    try {
      const stored = getCurrencyPreference();
      if (stored && currencyManager.isValidCurrency(stored)) {
        setSelectedCurrency(stored);
      }
    } catch {}
  }, []);

  const handleCurrencyChange = (currency: string) => {
    setSelectedCurrency(currency);
    storeCurrencyPreference(currency);
  };

  const handleBillingCycleChange = (cycle: BillingCycle) => {
    setBillingCycle(cycle);
    track({
      event: 'pricing_toggle',
      props: { event_category: 'engagement', event_label: cycle, billing_cycle: cycle }
    });
  };

  const handleBuy = (tier: PlanTier) => {
    // Redirect to signup with plan selection stored in URL params
    // After signup and company creation, onboarding will redirect to checkout with client_reference_id
    const signupUrl = new URL('/auth/signup', window.location.origin);
    signupUrl.searchParams.set('plan', tier.toLowerCase());
    signupUrl.searchParams.set('billing', billingCycle);

    window.location.href = signupUrl.toString();

    track({
      event: 'pricing_cta_click',
      props: {
        event_category: 'conversion',
        tier,
        billing_cycle: billingCycle,
        cta_label: tier === 'STARTER' ? 'Buy Starter' : 'Buy Team'
      }
    });
  };

  const currencies = currencyManager.getAvailableCurrencies();
  
  const getPricing = (tier: PlanTier) => {
    const monthlyPrice = Math.round(currencyManager.getPlanPrice(tier, selectedCurrency));
    const annualPrice = Math.round(currencyManager.getAnnualPrice(tier, selectedCurrency));
    const savings = Math.round(currencyManager.getAnnualSavings(tier, selectedCurrency));
    
    return {
      monthly: monthlyPrice,
      annual: annualPrice,
      savings,
      display: billingCycle === 'annual' ? annualPrice : monthlyPrice
    };
  };

  const starter = getPricing('STARTER');
  const pro = getPricing('PRO');

  const rawStarterFeatures = (t as unknown as { raw?: (key: string) => unknown }).raw?.('starter.features');
  const rawProFeatures = (t as unknown as { raw?: (key: string) => unknown }).raw?.('pro.features');
  const starterFeatures = (rawStarterFeatures as Record<string, string> | undefined) ?? {};
  const proFeatures = (rawProFeatures as Record<string, string> | undefined) ?? {};

  const plans = [
    {
      tier: 'STARTER' as PlanTier,
      name: t('starter.title'),
      limit: t('starter.limit'),
      description: t('starter.description'),
      pricing: starter,
      features: Object.values(starterFeatures),
      badge: null,
      highlighted: false,
    },
    {
      tier: 'PRO' as PlanTier,
      name: t('pro.title'),
      limit: t('pro.limit'),
      description: t('pro.description'),
      pricing: pro,
      features: Object.values(proFeatures),
      badge: t('pro.badge'),
      highlighted: true,
    },
  ];

  return (
    <section className="py-24 bg-background relative overflow-hidden">
      {/* Ambient Background */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
        <div className="absolute top-[10%] right-[5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[10%] left-[5%] w-72 h-72 bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
          {/* Header */}
          <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
              {t('title')}
            </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {t('subtitle')}
            </p>
            
          {/* Controls */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
            <div className="flex items-center gap-2 bg-muted/30 p-1 rounded-xl border border-border/50">
                  <button
                    onClick={() => handleBillingCycleChange('monthly')}
                className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      billingCycle === 'monthly'
                    ? 'bg-background text-foreground shadow-sm border border-border/50'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('monthly')}
                  </button>
                  <button
                    onClick={() => handleBillingCycleChange('annual')}
                className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-all relative ${
                      billingCycle === 'annual'
                    ? 'bg-background text-foreground shadow-sm border border-border/50'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                Annual
                <span className="absolute -top-3 -right-2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                  -15%
                </span>
                  </button>
              </div>

            {process.env.NEXT_PUBLIC_ENABLE_MARKETING_CURRENCY_SELECTOR === 'true' && (
                  <Select value={selectedCurrency} onValueChange={handleCurrencyChange}>
                <SelectTrigger className="w-[110px] bg-background border-border/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                  {currencies.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
            )}
          </div>
        </div>
        
        {/* Pricing Grid - 3 Columns (Starter, Pro, Pilot) */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12 items-start">
          
          {/* STARTER */}
          <div className="p-8 bg-background rounded-2xl border border-border hover:border-primary/20 transition-all duration-300 flex flex-col relative">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-foreground mb-2">{plans[0].name}</h3>
              <p className="text-sm text-muted-foreground h-10">{plans[0].description}</p>
            </div>
            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-foreground">
                  {formatPrice(plans[0].pricing.display, selectedCurrency, locale)}
                  </span>
                <span className="text-muted-foreground">/{billingCycle === 'annual' ? 'yr' : 'mo'}</span>
              </div>
              <p className="text-sm font-medium text-foreground mt-2">{plans[0].limit}</p>
            </div>
            <Button 
              onClick={() => handleBuy('STARTER')} 
              variant="outline" 
              className="w-full mb-8 border-border hover:bg-muted/50"
            >
              Buy Starter
            </Button>
            <div className="space-y-3 flex-grow">
              {plans[0].features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* PRO (HERO) */}
          <div className="p-8 bg-background rounded-2xl border-2 border-primary shadow-xl shadow-primary/5 scale-105 z-10 flex flex-col relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-sm">
              Most Popular
            </div>
            <div className="mb-6">
              <h3 className="text-xl font-bold text-foreground mb-2">{plans[1].name}</h3>
              <p className="text-sm text-muted-foreground h-10">{plans[1].description}</p>
                  </div>
            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-primary">
                  {formatPrice(plans[1].pricing.display, selectedCurrency, locale)}
                    </span>
                <span className="text-muted-foreground">/{billingCycle === 'annual' ? 'yr' : 'mo'}</span>
                  </div>
              <p className="text-sm font-medium text-foreground mt-2">{plans[1].limit}</p>
                </div>
                  <Button
              onClick={() => handleBuy('PRO')} 
              className="w-full mb-8 font-semibold shadow-lg shadow-primary/20"
                  >
              Buy Team
                  </Button>
            <div className="space-y-3 flex-grow">
              {plans[1].features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3 text-sm text-foreground font-medium">
                  <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>{feature}</span>
                </div>
            ))}
            </div>
          </div>

          {/* PILOT / ENTERPRISE */}
          <div className="p-8 bg-muted/10 rounded-2xl border border-border hover:border-blue-500/20 transition-all duration-300 flex flex-col relative">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-foreground mb-2">{t('pilot.title')}</h3>
              <p className="text-sm text-muted-foreground h-10">{t('pilot.subtitle')}</p>
            </div>
            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-foreground">
                  {formatPrice(currencyManager.getPilotLow(selectedCurrency), selectedCurrency, locale)}
                </span>
                <span className="text-muted-foreground">+</span>
              </div>
              <p className="text-sm font-medium text-foreground mt-2">One-time fee</p>
            </div>
                  <a
                    href="/contact?topic=pilot"
              className="w-full mb-8 inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 bg-background border border-input hover:bg-accent hover:text-accent-foreground"
            >
              Contact Sales
            </a>
            <div className="space-y-3 flex-grow">
              <div className="flex items-start gap-3 text-sm text-muted-foreground">
                <ShieldCheck className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <span>Validation Plan & Report</span>
                </div>
              <div className="flex items-start gap-3 text-sm text-muted-foreground">
                <Building2 className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <span>Dedicated Tenant Setup</span>
              </div>
              <div className="flex items-start gap-3 text-sm text-muted-foreground">
                <Sparkles className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <span>White-Glove Onboarding</span>
          </div>
              <div className="flex items-start gap-3 text-sm text-muted-foreground">
                <Check className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <span>90 Days Pilot Access</span>
                  </div>
                </div>
                </div>
                </div>

        {/* Onboarding Banner */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-background rounded-xl border border-border p-6 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Sparkles className="w-6 h-6 text-primary" />
                </div>
              <div>
                <h4 className="text-lg font-semibold mb-1">{t('addons.onboardingTitle')}</h4>
                <p className="text-sm text-muted-foreground max-w-md">
                  {t('addons.onboardingSubtitle')} Starting from {formatPrice(currencyManager.getOnboardingStandard(selectedCurrency), selectedCurrency, locale)}.
                </p>
              </div>
          </div>
            <a href="/contact?topic=onboarding" className="text-sm font-semibold text-primary hover:underline whitespace-nowrap flex items-center gap-1">
              Learn about onboarding <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Trust Footer */}
          <div className="mt-12 pt-8 border-t border-border/50 text-center">
            <p className="text-sm font-medium text-muted-foreground mb-6">{t('footer.title')}</p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 text-sm text-muted-foreground/80">
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> {t('footer.dataExport')}</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> {t('footer.hosting')}</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> {t('footer.support')}</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-green-500" /> {t('riskReversal')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
